import { Text } from '@chakra-ui/react';
import { PatchDataWrapper } from '@diamond/shared/data-access';
import {
  BackofficeActivitiesOrderItem,
  BackofficeOrderUpdate,
} from '@diamond/shared/types';
import { QueryKey, useQueryClient } from '@tanstack/react-query';
import { NumericFormat } from 'react-number-format';

import { ORDER_CHANGELOG_QUERY_KEY } from '../../../api';
import {
  useUpdateActivitiesDetailColumn,
  useUpdateActivitiesDetailQueryData,
} from '../../../hooks';
import { EditableColumnProps } from '../../../types/activity-column-types';
import { formatCurrency } from '../../../utils/activity-formatters';

const API_URL = 'backoffice/order';

interface Props
  extends EditableColumnProps<
    BackofficeActivitiesOrderItem,
    number | undefined
  > {
  queryKey?: QueryKey;
  activitiesId: string;
  token?: string;
  refetch: () => void;
}

export function LatestPriceColumn({
  info,
  selectedData,
  onUpdate,
  queryKey = [],
  activitiesId,
  token,
  refetch,
}: Props) {
  const queryClient = useQueryClient();

  const { patchData } = PatchDataWrapper<BackofficeOrderUpdate>(
    API_URL,
    token as string,
    'admin'
  );

  const isSelected = selectedData.some(
    (row) => row.original.id === info.row.original.id
  );
  const canOverwritePrice = info.row.original.can_overwrite_price;

  // Optimistic update for query cache
  const setQueryData = useUpdateActivitiesDetailQueryData({
    queryKey,
    info,
    name: 'latest_price',
  });

  // Debounced update handler
  const setValue = useUpdateActivitiesDetailColumn({
    onUpdate(value) {
      setQueryData(value);
      patchData(
        { latest_price: value },
        undefined,
        `${activitiesId}/item/${info.row.original.id}`
      ).then(async () => {
        refetch();
        await queryClient.invalidateQueries({
          queryKey: [ORDER_CHANGELOG_QUERY_KEY],
        });
      });
    },
  });

  if (isSelected && canOverwritePrice) {
    const handlePriceUpdate = (values: { floatValue: number | undefined }) => {
      setValue(String(values.floatValue ?? 0));
    };

    const priceValue = info.row.original.is_discount
      ? info.row.original.price_after_discount ?? 0
      : info.getValue();

    return (
      <div className="px-1">
        <NumericFormat
          key={`price-${info.row.original.id}-${priceValue}`}
          prefix="Rp"
          thousandSeparator="."
          decimalSeparator=","
          style={{
            borderColor: 'gray',
            borderWidth: 1,
            borderRadius: 4,
            padding: 4,
          }}
          allowNegative={false}
          defaultValue={priceValue || 0}
          onValueChange={handlePriceUpdate}
        />
        <Text className="text-xs" fontSize={12} mt="4px" color="red.500">
          *Input harga final
        </Text>
      </div>
    );
  }

  const displayValue = info.row.original.is_discount
    ? info.row.original.price_after_discount ?? 0
    : info.row.original.quantity_free_goods
    ? 0
    : info.getValue();

  return (
    <Text fontSize="sm" fontWeight="400" color="#373F41">
      {formatCurrency(displayValue || 0)}
    </Text>
  );
}
